import { Avatar } from "@hakistudio/hakiui";
import { NavLink } from "react-router-dom";
import { authClient } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { useProfile } from "@/lib/queries";

type Props = {
  /**
   * `full` is the sidebar row — avatar beside the name and email.
   * `compact` is the mobile top bar, where only the avatar fits.
   */
  variant?: "full" | "compact";
  className?: string;
};

/**
 * The signed-in user's avatar, linking to Settings.
 *
 * Falls back through profile -> session -> nothing so the row never renders as
 * an empty slot: `/v1/me` is a network round trip, but the session is already in
 * memory by the time the shell mounts, so its name and email fill the gap
 * instead of a skeleton that would flash on every navigation.
 */
export function ProfileButton({ variant = "full", className }: Props) {
  const { data: profile } = useProfile();
  const { data: session } = authClient.useSession();

  const user = session?.user;
  const name = profile?.displayName ?? user?.name ?? user?.email ?? "Account";
  const email = user?.email ?? null;
  // Neon Auth's `image` is the Google picture; our own profile row wins because
  // it is the one the user can actually change.
  const avatarUrl = profile?.avatarUrl ?? user?.image ?? undefined;

  if (variant === "compact") {
    return (
      <NavLink
        to="/settings"
        aria-label={`Account — ${name}`}
        title={name}
        className={cn("rounded-pill focus-visible:outline-offset-4", className)}
      >
        <Avatar src={avatarUrl} name={name} size="md" radius="full" />
      </NavLink>
    );
  }

  // No active styling: this points at /settings, which the nav item above
  // already highlights. Marking both would light up two rows for one route.
  return (
    <NavLink
      to="/settings"
      className={cn(
        "flex items-center gap-3 rounded-pill px-2 py-2 text-left transition-colors hover:bg-sunken",
        className,
      )}
    >
      <Avatar src={avatarUrl} name={name} size="md" radius="full" />
      {/* min-w-0 lets the truncation below actually take effect inside the flex row. */}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-sm font-medium text-ink">{name}</span>
        {email ? <span className="truncate text-xs text-ink-faint">{email}</span> : null}
      </span>
    </NavLink>
  );
}
